import type { FeedEntry } from '~/services/layout';
import { useEffect, useRef } from 'react';
import Hls from 'hls.js';

type UseAudioPlayerAudioSyncParams = {
  audioRef: React.RefObject<HTMLAudioElement | null>;
  entry: FeedEntry | null;
  isPlaying: boolean;
  isMuted: boolean;
  volume: number;
  playbackRate: number;
  setIsSpeedMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setPlaybackSpeedOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsFavorited: React.Dispatch<React.SetStateAction<boolean>>;
  setIsPlaylistAdded: React.Dispatch<React.SetStateAction<boolean>>;
  setCurrentTime: React.Dispatch<React.SetStateAction<number>>;
  setDuration: React.Dispatch<React.SetStateAction<number>>;
  setIsPlaying: React.Dispatch<React.SetStateAction<boolean>>;
};

/** Synchronizes source, playback, volume, and rate with the underlying audio element. */
export function useAudioPlayerAudioSync({
  audioRef,
  entry,
  isPlaying,
  isMuted,
  volume,
  playbackRate,
  setIsSpeedMenuOpen,
  setPlaybackSpeedOpen,
  setIsFavorited,
  setIsPlaylistAdded,
  setCurrentTime,
  setDuration,
  setIsPlaying,
}: UseAudioPlayerAudioSyncParams): void {
  const isSourceSwitchingRef = useRef<boolean>(false);
  const hlsRef = useRef<Hls | null>(null);
  const isHlsSourceReadyRef = useRef<boolean>(false);

  const isHlsSource = (src: string): boolean => src.includes('.m3u8');

  const destroyHlsInstance = (): boolean => {
    const hadHlsInstance = Boolean(hlsRef.current);

    hlsRef.current?.destroy();
    hlsRef.current = null;
    isHlsSourceReadyRef.current = false;

    return hadHlsInstance;
  };

  // Intentionally keyed by entry only: including isPlaying or other state here
  // causes source reloads on pause/play toggles and resets playback position.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!entry?.content?.src || !audioRef.current) {
      destroyHlsInstance();
      return;
    }

    const hadHlsInstance = destroyHlsInstance();

    if (hadHlsInstance) {
      // hls.js attaches a MediaSource/blob URL directly to the audio element.
      // Clear it before loading a non-HLS source so native playback can recover.
      audioRef.current.removeAttribute('src');
    }

    isSourceSwitchingRef.current = true;

    setIsSpeedMenuOpen(false);
    setPlaybackSpeedOpen(false);
    setIsFavorited(false);
    setIsPlaylistAdded(false);

    if (isHlsSource(entry.content.src) && Hls.isSupported()) {
      const hls = new Hls();
      hlsRef.current = hls;
      isHlsSourceReadyRef.current = false;

      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        hls.loadSource(entry.content!.src);
      });

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        isHlsSourceReadyRef.current = true;

        if (!audioRef.current) {
          isSourceSwitchingRef.current = false;
          return;
        }

        audioRef.current.playbackRate = playbackRate;

        if (!isPlaying) {
          isSourceSwitchingRef.current = false;
          return;
        }

        audioRef.current
          .play()
          .then(() => {
            isSourceSwitchingRef.current = false;
          })
          .catch(() => {
            isSourceSwitchingRef.current = false;
            setIsPlaying(false);
          });
      });

      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (!data?.fatal) {
          return;
        }

        isSourceSwitchingRef.current = false;
        setIsPlaying(false);
      });

      hls.attachMedia(audioRef.current);
      setCurrentTime(0);
      setDuration(0);

      return;
    }

    audioRef.current.load();
    audioRef.current.playbackRate = playbackRate;
    setCurrentTime(0);
    setDuration(0);

    if (!isPlaying) {
      isSourceSwitchingRef.current = false;
    }
  }, [entry]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!audioRef.current || !entry?.content?.src) return;

    if (hlsRef.current && !isHlsSourceReadyRef.current) {
      return;
    }

    if (!isPlaying) {
      if (isSourceSwitchingRef.current) {
        return;
      }

      audioRef.current.pause();
      return;
    }

    audioRef.current
      .play()
      .then(() => {
        isSourceSwitchingRef.current = false;
      })
      .catch((error: unknown) => {
        if (
          error instanceof DOMException &&
          error.name === 'AbortError' &&
          isSourceSwitchingRef.current
        ) {
          return;
        }

        isSourceSwitchingRef.current = false;
        setIsPlaying(false);
      });
  }, [audioRef, entry, isPlaying, setIsPlaying]);

  useEffect(() => {
    if (!audioRef.current) return;

    audioRef.current.volume = volume;
    audioRef.current.muted = isMuted;
  }, [audioRef, volume, isMuted]);

  useEffect(() => {
    if (!audioRef.current) return;

    audioRef.current.playbackRate = playbackRate;
  }, [audioRef, playbackRate]);

  useEffect(() => {
    return () => {
      destroyHlsInstance();
    };
  }, []);
}
